import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import FontAwesomeIcon from "../../components/FontAwesomeIcon";
import BookingModal from '../../components/BookingModal';

const HotAirBalloon = () => {
  const [isModalOpen, setIsModalOpen] = useState(false);

  return (
    <div>
      {/* Hero Section */}
      <section
        className="relative h-96 md:h-[500px] bg-cover bg-center flex items-center"
        style={{
          backgroundImage: 'linear-gradient(rgba(0,0,0,0.4), rgba(0,0,0,0.4)), url(https://images.unsplash.com/photo-1506905925346-21bda4d32df4?w=1600)',
        }}
      >
        <div className="container mx-auto px-4">
          <div className="max-w-4xl text-white">
            <h1 className="text-3xl md:text-5xl font-bold mb-3 font-heading">
              Hot Air Balloon Safari
            </h1>
            <p className="text-xl md:text-2xl mb-6">
              Soar silently above the African savanna at sunrise for the ultimate wildlife perspective
            </p>
            <div className="flex items-center gap-6 mb-6">
              <div className="flex items-center gap-2">
                <div className="flex text-safari-gold">
                  {[...Array(5)].map((_, i) => (
                    <FontAwesomeIcon key={i} icon="fa-star" className="w-5 h-5" />
                  ))}
                </div>
                <span className="text-lg">(300+ reviews)</span>
              </div>
            </div>
            <div className="flex flex-wrap gap-4 mb-8">
              <div className="bg-white/90 backdrop-blur-sm text-safari-green px-4 py-2 rounded-full text-sm font-semibold">
                <FontAwesomeIcon icon="fa-champagne-glasses" className="w-4 h-4 mr-2" />
                Bush Breakfast Included
              </div>
              <div className="bg-white/90 backdrop-blur-sm text-safari-green px-4 py-2 rounded-full text-sm font-semibold">
                <FontAwesomeIcon icon="fa-camera" className="w-4 h-4 mr-2" />
                Best Photo Ops
              </div>
              <div className="bg-white/90 backdrop-blur-sm text-safari-green px-4 py-2 rounded-full text-sm font-semibold">
                <FontAwesomeIcon icon="fa-certificate" className="w-4 h-4 mr-2" />
                Flight Certificate
              </div>
            </div>
            <button
              onClick={() => setIsModalOpen(true)}
              className="bg-safari-gold hover:bg-safari-gold/90 text-white px-8 py-4 rounded-full text-lg font-bold transition-all duration-300 shadow-lg hover:shadow-xl"
            >
              Book Your Flight
            </button>
          </div>
        </div>
      </section>

      {/* Page Content */}
      <div className="container mx-auto px-4 py-16">
        <div className="lg:grid lg:grid-cols-3 lg:gap-12">
          {/* Main Content */}
          <main className="lg:col-span-2">
            {/* Description Section */}
            <section className="mb-16">
              <h2 className="text-3xl md:text-4xl font-bold text-safari-green mb-8">
                The Ultimate African Safari Perspective
              </h2>
              <div className="prose prose-lg max-w-none">
                <p className="text-lg text-gray-700 leading-relaxed mb-6">
                  A hot air balloon safari offers a unique perspective over Uganda's premier national parks as the sun paints the sky in shades of gold and pink. The African wilderness wakes up below: herds of Uganda kobs graze on the Kasenyi Plains, elephants move in families through the euphorbia trees, and buffalo herds leave trails in the morning dew. This aerial perspective transforms the landscape into a living map of nature, offering views and photographic opportunities impossible from any ground-based vehicle.
                </p>
                <p className="text-lg text-gray-700 leading-relaxed mb-6">
                  The adventure begins in the cool pre-dawn darkness, typically around 5:00 AM. The massive balloon inflates against the starlit sky, with each burst of flame from the burner adding to the spectacle. The crew's practiced movements, the whoosh of hot air filling the envelope, and the gradual rise of the colorful fabric create an impressive display. Then, with a gentle lift, the balloon becomes airborne. Unlike a game drive vehicle, the balloon moves with the wind, offering a serene and completely unobtrusive way to observe wildlife in their natural behavior.
                </p>
                <h3 className="text-2xl font-bold text-safari-green mt-8 mb-4">Where You'll Fly</h3>
                <p className="text-lg text-gray-700 leading-relaxed mb-6">
                  Hot air balloon safaris operate in two of Uganda's most spectacular national parks. In Queen Elizabeth National Park, operated by Balloon Tours Uganda Ltd, you'll float over the Kasenyi Plains—one of East Africa's richest wildlife areas. The balloon can accommodate up to 16 passengers and offers breathtaking views of the Rwenzori Mountains, crater lakes, and the Kazinga Channel. You might drift low over a crater lake to see hippos returning to the water or soar high for panoramic views of Lake George and the Great Rift Valley escarpment.
                </p>
                <p className="text-lg text-gray-700 leading-relaxed mb-6">
                  In Murchison Falls National Park, Dream Balloon Uganda operates flights over the northern bank of the Victoria Nile. Their smaller balloons (up to 8 passengers) provide a more intimate experience, floating over the savanna where you can spot elephants, giraffes, Jackson's hartebeest, and even the rare shoebill stork along the riverbanks. The views extend to Lake Albert and the dramatic Murchison Falls themselves. Both sunrise and sunset flights are available here, though the sunrise flights with bush breakfast remain the most popular.
                </p>
                <h3 className="text-2xl font-bold text-safari-green mt-8 mb-4">The Bush Breakfast Tradition</h3>
                <p className="text-lg text-gray-700 leading-relaxed">
                  The experience doesn't end when the balloon touches down. In the tradition of early balloonists, guests are treated to a "bush breakfast" right in the heart of the park. A white-linen table is set up in the middle of the African wilderness, surrounded by acacia trees, with the very animals just viewed from above often grazing nearby. Chefs prepare a full English breakfast complete with eggs, sausages, bacon, fresh tropical fruits, pastries, and juice. The meal is accompanied by sparkling wine or champagne—a ballooning tradition dating back to the 18th century. Participants also receive a personalized flight certificate signed by the pilot, commemorating the aerial adventure.
                </p>
              </div>
            </section>

            {/* Highlights */}
            <section className="mb-16">
              <h2 className="text-3xl md:text-4xl font-bold text-safari-green mb-12 text-center">
                Flight Highlights
              </h2>
              <div className="grid md:grid-cols-2 gap-8">
                {[
                  {
                    icon: 'fa-sun',
                    title: 'Sunrise Departure',
                    desc: 'Witness the African dawn from the sky as the sun breaks over the horizon. The golden hour provides the best light for photography and the coolest temperatures for optimal flight conditions.'
                  },
                  {
                    icon: 'fa-binoculars',
                    title: 'Aerial Wildlife Viewing',
                    desc: 'Spot elephants, lions, buffalo, Uganda kobs, and antelope herds from a unique bird\'s-eye view. The silent approach allows you to observe natural behavior undisturbed by vehicle noise.'
                  },
                  {
                    icon: 'fa-champagne-glasses',
                    title: 'Champagne Bush Breakfast',
                    desc: 'Celebrate your landing with a full English breakfast and sparkling wine served on white linen in the wild. Chefs prepare fresh food while you watch wildlife around you.'
                  },
                  {
                    icon: 'fa-mountain',
                    title: 'Panoramic Landscapes',
                    desc: 'See the Great Rift Valley, volcanic crater lakes, and snow-capped Rwenzori Mountains stretching to the horizon. Views extend for dozens of kilometers on clear mornings.'
                  },
                  {
                    icon: 'fa-certificate',
                    title: 'Flight Certificate',
                    desc: 'Receive a commemorative certificate signed by your pilot, documenting your flight date, location, and duration—a cherished memento of your aerial adventure.'
                  },
                  {
                    icon: 'fa-user-shield',
                    title: 'Expert Licensed Pilots',
                    desc: 'Flown by highly experienced pilots licensed by Uganda Civil Aviation Authority with thousands of flight hours. All operations meet international safety standards.'
                  }
                ].map((highlight, index) => (
                  <div key={index} className="flex gap-4">
                    <div className="flex-shrink-0">
                      <div className="w-12 h-12 bg-safari-green rounded-full flex items-center justify-center">
                        <FontAwesomeIcon icon={highlight.icon} className="w-6 h-6 text-white" />
                      </div>
                    </div>
                    <div>
                      <h3 className="text-xl font-bold text-safari-green mb-2">{highlight.title}</h3>
                      <p className="text-gray-700">{highlight.desc}</p>
                    </div>
                  </div>
                ))}
              </div>
            </section>

            {/* Itinerary */}
            <section className="mb-16">
              <h2 className="text-3xl md:text-4xl font-bold text-safari-green mb-8 text-center">
                Your Flight Experience
              </h2>
              <div className="bg-safari-light border-l-4 border-safari-gold p-6 mb-8 rounded-r-lg">
                <p className="text-gray-700 text-lg">
                  <strong>⚠️ Important Note:</strong> This itinerary is tentative and can be customized based on your preferences
                  and pickup location. Flight times depend on weather conditions and may be adjusted for safety.
                </p>
              </div>
              <div className="space-y-6">
                {[
                  {
                    time: '05:00 AM',
                    title: 'Pickup & Transfer',
                    desc: 'Early morning pickup from your lodge. Transfer to the launch site at Kasenyi Gate (Queen Elizabeth) or northern bank of the Nile (Murchison Falls). Enjoy the pre-dawn wildlife activity en route.'
                  },
                  {
                    time: '05:30 AM',
                    title: 'Inflation & Briefing',
                    desc: 'Watch the balloon fill with hot air while enjoying complimentary coffee or tea. Your pilot conducts a comprehensive safety briefing covering boarding procedures, flight positions, and landing protocols.'
                  },
                  {
                    time: '06:15 AM',
                    title: 'Sunrise Lift-Off',
                    desc: 'Ascend gently as the sun breaks the horizon, painting the sky in brilliant colors. Begin your 45-60 minute flight over the plains, with altitude varying from treetop level to 1,000+ feet for panoramic views.'
                  },
                  {
                    time: '07:15 AM',
                    title: 'Landing & Celebration',
                    desc: 'Touch down gently in the savanna. Celebrate with a traditional champagne toast—a ballooning custom dating back to 18th century France. Receive your personalized flight certificate.'
                  },
                  {
                    time: '07:45 AM',
                    title: 'Bush Breakfast',
                    desc: 'Sit down to a white-linen breakfast cooked fresh in the bush. Full English breakfast with eggs, sausages, bacon, fruits, pastries, and juice. Unbeatable atmosphere with wildlife grazing nearby.'
                  },
                  {
                    time: '09:00 AM',
                    title: 'Game Drive Return',
                    desc: 'Enjoy a mini game drive as you transfer back to your lodge. Your guide will stop for wildlife sightings and photo opportunities, maximizing your safari experience.'
                  }
                ].map((step, index) => (
                  <div key={index} className="card">
                    <div className="p-6 flex gap-4">
                      <div className="flex-shrink-0">
                        <div className="w-16 h-16 bg-safari-green rounded-full flex items-center justify-center text-white font-bold text-sm text-center leading-tight p-1">
                          {step.time.split(' ')[0]}<br />{step.time.split(' ')[1]}
                        </div>
                      </div>
                      <div className="flex-grow">
                        <h3 className="text-xl font-bold text-safari-green mb-2">{step.title}</h3>
                        <p className="text-gray-700">{step.desc}</p>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            </section>

            {/* What to Bring */}
            <section className="mb-16">
              <h2 className="text-3xl md:text-4xl font-bold text-safari-green mb-8 text-center">
                What to Bring
              </h2>
              <div className="grid md:grid-cols-2 gap-8">
                <div className="card p-6">
                  <h3 className="text-xl font-bold text-safari-green mb-4 flex items-center gap-2">
                    <FontAwesomeIcon icon="fa-shirt" className="w-6 h-6" />
                    Clothing Essentials
                  </h3>
                  <ul className="space-y-3 text-gray-700">
                    <li className="flex items-start gap-2">
                      <FontAwesomeIcon icon="fa-check" className="w-4 h-4 text-safari-gold mt-1" />
                      <span><strong>Layered clothing:</strong> Mornings are cool (10-15°C) but warm up quickly. Bring a light jacket or fleece and long pants.</span>
                    </li>
                    <li className="flex items-start gap-2">
                      <FontAwesomeIcon icon="fa-check" className="w-4 h-4 text-safari-gold mt-1" />
                      <span><strong>Hat or cap:</strong> Protect your head from the burner heat above. A baseball cap or wide-brimmed hat works well.</span>
                    </li>
                    <li className="flex items-start gap-2">
                      <FontAwesomeIcon icon="fa-check" className="w-4 h-4 text-safari-gold mt-1" />
                      <span><strong>Comfortable colors:</strong> Neutral earth tones (khaki, brown, green) are best for wildlife viewing and photography.</span>
                    </li>
                  </ul>
                </div>

                <div className="card p-6">
                  <h3 className="text-xl font-bold text-safari-green mb-4 flex items-center gap-2">
                    <FontAwesomeIcon icon="fa-hiking" className="w-6 h-6" />
                    Footwear & Accessories
                  </h3>
                  <ul className="space-y-3 text-gray-700">
                    <li className="flex items-start gap-2">
                      <FontAwesomeIcon icon="fa-check" className="w-4 h-4 text-safari-gold mt-1" />
                      <span><strong>Sturdy closed shoes:</strong> Sneakers or hiking boots required. No sandals, high heels, or flip-flops for safety reasons.</span>
                    </li>
                    <li className="flex items-start gap-2">
                      <FontAwesomeIcon icon="fa-check" className="w-4 h-4 text-safari-gold mt-1" />
                      <span><strong>Sunglasses:</strong> Essential for the bright morning sun and glare off the landscape.</span>
                    </li>
                    <li className="flex items-start gap-2">
                      <FontAwesomeIcon icon="fa-check" className="w-4 h-4 text-safari-gold mt-1" />
                      <span><strong>Sunscreen:</strong> High SPF recommended. UV exposure is strong at altitude.</span>
                    </li>
                  </ul>
                </div>

                <div className="card p-6">
                  <h3 className="text-xl font-bold text-safari-green mb-4 flex items-center gap-2">
                    <FontAwesomeIcon icon="fa-camera" className="w-6 h-6" />
                    Photography Equipment
                  </h3>
                  <ul className="space-y-3 text-gray-700">
                    <li className="flex items-start gap-2">
                      <FontAwesomeIcon icon="fa-check" className="w-4 h-4 text-safari-gold mt-1" />
                      <span><strong>Camera with zoom lens:</strong> 70-200mm or longer ideal for wildlife. Wide-angle for landscapes.</span>
                    </li>
                    <li className="flex items-start gap-2">
                      <FontAwesomeIcon icon="fa-check" className="w-4 h-4 text-safari-gold mt-1" />
                      <span><strong>Extra batteries:</strong> Cold morning temperatures drain batteries faster. Bring fully charged spares.</span>
                    </li>
                    <li className="flex items-start gap-2">
                      <FontAwesomeIcon icon="fa-check" className="w-4 h-4 text-safari-gold mt-1" />
                      <span><strong>Memory cards:</strong> Bring plenty of storage. You'll take hundreds of photos from this unique perspective.</span>
                    </li>
                  </ul>
                </div>

                <div className="card p-6">
                  <h3 className="text-xl font-bold text-safari-green mb-4 flex items-center gap-2">
                    <FontAwesomeIcon icon="fa-briefcase" className="w-6 h-6" />
                    Other Essentials
                  </h3>
                  <ul className="space-y-3 text-gray-700">
                    <li className="flex items-start gap-2">
                      <FontAwesomeIcon icon="fa-check" className="w-4 h-4 text-safari-gold mt-1" />
                      <span><strong>Binoculars:</strong> Optional but recommended for spotting distant wildlife and birds.</span>
                    </li>
                    <li className="flex items-start gap-2">
                      <FontAwesomeIcon icon="fa-check" className="w-4 h-4 text-safari-gold mt-1" />
                      <span><strong>Small backpack:</strong> For carrying your camera, jacket, and personal items during the flight.</span>
                    </li>
                    <li className="flex items-start gap-2">
                      <FontAwesomeIcon icon="fa-check" className="w-4 h-4 text-safari-gold mt-1" />
                      <span><strong>Sense of adventure:</strong> Most important! Come ready for an unforgettable experience.</span>
                    </li>
                  </ul>
                </div>
              </div>
            </section>

            {/* FAQs */}
            <section className="mb-16">
              <h2 className="text-3xl md:text-4xl font-bold text-safari-green mb-12 text-center">
                Common Questions
              </h2>
              <div className="space-y-6">
                <div className="border-l-4 border-safari-gold pl-6 py-4">
                  <h3 className="text-xl font-bold text-safari-green mb-2">
                    Is hot air ballooning safe?
                  </h3>
                  <p className="text-gray-700">
                    Yes, extremely safe. All hot air balloon operations in Uganda are regulated by the Uganda Civil Aviation Authority and meet international safety standards. The balloons are maintained to strict specifications and inspected regularly. Pilots are licensed by both Ugandan and international authorities and have thousands of flight hours of experience. Flights only operate in safe weather conditions—if winds exceed 10 knots, there's rain, or visibility is poor, flights are cancelled and rescheduled. The actual flight is very gentle and stable, with no sudden movements.
                  </p>
                </div>

                <div className="border-l-4 border-safari-gold pl-6 py-4">
                  <h3 className="text-xl font-bold text-safari-green mb-2">
                    What happens if the weather is bad?
                  </h3>
                  <p className="text-gray-700">
                    Safety is paramount. Weather conditions are assessed early each morning before inflation begins. If wind speeds are too high (over 10 knots), there's rain or approaching storms, or visibility is less than 4 kilometers, the flight will be cancelled. You'll be offered a full refund or the option to reschedule for the next available morning (subject to availability). The dry seasons (June-September and December-February) offer the most reliable weather, though flights operate year-round when conditions permit.
                  </p>
                </div>

                <div className="border-l-4 border-safari-gold pl-6 py-4">
                  <h3 className="text-xl font-bold text-safari-green mb-2">
                    How high do we fly and what will we see?
                  </h3>
                  <p className="text-gray-700">
                    Altitude varies throughout the flight based on wind conditions and wildlife viewing opportunities. Your pilot will fly low over the trees—sometimes just a few meters up—to spot wildlife like elephants, lions, and buffalo without disturbing them. Then you'll climb to 300-1,000 feet or more for spectacular panoramic views of the landscape, crater lakes, rivers, and mountains. From Queen Elizabeth, you'll see the Rwenzori Mountains and Kazinga Channel. From Murchison Falls, you'll see Lake Albert and the Nile. The silent approach allows you to observe natural animal behavior impossible to see from vehicles.
                  </p>
                </div>

                <div className="border-l-4 border-safari-gold pl-6 py-4">
                  <h3 className="text-xl font-bold text-safari-green mb-2">
                    What should I wear and bring?
                  </h3>
                  <p className="text-gray-700">
                    Dress in comfortable layers as mornings start cool (10-15°C) but warm up quickly once the sun rises. Long pants and a light jacket or fleece are recommended. Wear sturdy closed-toe shoes (sneakers or hiking boots)—no sandals, high heels, or flip-flops for safety reasons. Bring a hat to protect your head from the burner heat above. Don't forget your camera with extra batteries and memory cards—you'll want to capture this once-in-a-lifetime perspective. Sunglasses and sunscreen are also essential. Everything you bring must fit in a small backpack as space in the basket is limited.
                  </p>
                </div>

                <div className="border-l-4 border-safari-gold pl-6 py-4">
                  <h3 className="text-xl font-bold text-safari-green mb-2">
                    Can children participate? Are there any health restrictions?
                  </h3>
                  <p className="text-gray-700">
                    The minimum age is typically 6 years old, and children aged 6-12 often receive a 50% discount. Children must be tall enough to see over the basket edge (approximately 1.2 meters) and able to follow safety instructions. The activity is not suitable for pregnant women or people with serious back problems, as landings can occasionally be bumpy. You must be able to climb into the basket using a ladder and stand for the duration of the flight (45-60 minutes). There are no upper age limits—many seniors enjoy balloon safaris. If you have any health concerns, consult your doctor before booking.
                  </p>
                </div>

                <div className="border-l-4 border-safari-gold pl-6 py-4">
                  <h3 className="text-xl font-bold text-safari-green mb-2">
                    Which park is better for ballooning—Queen Elizabeth or Murchison Falls?
                  </h3>
                  <p className="text-gray-700">
                    Both offer spectacular but different experiences. Queen Elizabeth National Park (Balloon Tours Uganda Ltd) features the Kasenyi Plains with high concentrations of Uganda kobs, elephants, and buffalo, plus stunning views of crater lakes and the Rwenzori Mountains. Balloons hold up to 16 passengers. Murchison Falls National Park (Dream Balloon Uganda) offers flights over the northern bank of the Victoria Nile with giraffes, elephants, Jackson's hartebeest, and possible shoebill sightings. Smaller balloons (8 passengers) provide a more intimate experience. Both include bush breakfast and champagne. Choose based on your overall safari itinerary—if you're visiting both parks, consider ballooning in whichever you visit first for the best weather odds.
                  </p>
                </div>
              </div>
            </section>

            {/* Related Activities */}
            <section>
              <h2 className="text-3xl md:text-4xl font-bold text-safari-green mb-8 text-center">
                More Safari Adventures
              </h2>
              <div className="grid md:grid-cols-2 gap-8">
                <Link to="/activities/kazinga-boat-safari" className="card group hover:shadow-xl transition-all duration-300">
                  <div className="relative h-48 overflow-hidden">
                    <img
                      src="https://images.unsplash.com/photo-1576485290814-1c72aa4bbb8e?w=800"
                      alt="Kazinga Channel"
                      className="w-full h-full object-cover group-hover:scale-110 transition-transform duration-500"
                    />
                  </div>
                  <div className="p-6">
                    <h3 className="text-xl font-bold text-safari-green mb-2">Kazinga Boat Safari</h3>
                    <p className="text-gray-700 mb-4">Cruise the channel connecting Lakes Edward and George, seeing hippos, elephants, and crocodiles.</p>
                    <p className="text-safari-gold font-bold">Activity fees apply</p>
                  </div>
                </Link>

                <Link to="/activities/lion-tracking" className="card group hover:shadow-xl transition-all duration-300">
                  <div className="relative h-48 overflow-hidden">
                    <img
                      src="https://images.unsplash.com/photo-1546182990-dffeafbe841d?w=800"
                      alt="Lion Tracking"
                      className="w-full h-full object-cover group-hover:scale-110 transition-transform duration-500"
                    />
                  </div>
                  <div className="p-6">
                    <h3 className="text-xl font-bold text-safari-green mb-2">Lion Tracking Experience</h3>
                    <p className="text-gray-700 mb-4">Join researchers tracking radio-collared tree-climbing lions in Queen Elizabeth National Park.</p>
                    <p className="text-safari-gold font-bold">Activity fees apply</p>
                  </div>
                </Link>
              </div>
            </section>
          </main>

          {/* Sidebar */}
          <aside className="lg:col-span-1">
            <div className="sticky top-24">
              <div className="card p-6 mb-8">
                <h3 className="text-2xl font-bold text-safari-green mb-4">Activity Details</h3>
                <div className="space-y-4 text-gray-700">
                  <div className="flex items-center gap-3">
                    <FontAwesomeIcon icon="fa-clock" className="w-6 h-6 text-safari-green" />
                    <span><strong>Duration:</strong> 3-4 hours total</span>
                  </div>
                  <div className="flex items-center gap-3">
                    <FontAwesomeIcon icon="fa-plane" className="w-6 h-6 text-safari-green" />
                    <span><strong>Flight Time:</strong> 45-60 minutes</span>
                  </div>
                  <div className="flex items-center gap-3">
                    <FontAwesomeIcon icon="fa-calendar" className="w-6 h-6 text-safari-green" />
                    <span><strong>Best Time:</strong> Jun-Sep, Dec-Feb</span>
                  </div>
                  <div className="flex items-center gap-3">
                    <FontAwesomeIcon icon="fa-dollar-sign" className="w-6 h-6 text-safari-green" />
                    <span><strong>Activity Fee:</strong> Contact for current rates</span>
                  </div>
                  <div className="flex items-center gap-3">
                    <FontAwesomeIcon icon="fa-chart-simple" className="w-6 h-6 text-safari-green" />
                    <span><strong>Difficulty:</strong> Easy</span>
                  </div>
                  <div className="flex items-center gap-3">
                    <FontAwesomeIcon icon="fa-users" className="w-6 h-6 text-safari-green" />
                    <span><strong>Capacity:</strong> 8-16 passengers</span>
                  </div>
                  <div className="flex items-center gap-3">
                    <FontAwesomeIcon icon="fa-user" className="w-6 h-6 text-safari-green" />
                    <span><strong>Min Age:</strong> 6 years</span>
                  </div>
                </div>
                <button
                  onClick={() => setIsModalOpen(true)}
                  className="bg-safari-gold hover:bg-safari-gold/90 text-white px-8 py-4 rounded-full text-lg font-bold transition-all duration-300 shadow-lg hover:shadow-xl w-full mt-6"
                >
                  Book Balloon Safari
                </button>
              </div>

              <div className="card p-6 mb-8">
                <h3 className="text-2xl font-bold text-safari-green mb-6 flex items-center gap-2">
                  <FontAwesomeIcon icon="fa-check-circle" className="w-6 h-6 text-green-600" />
                  What's Included
                </h3>
                <ul className="space-y-3">
                  {[
                    "45-60 minute hot air balloon flight",
                    "Bush breakfast with champagne",
                    "Transfers from nearby lodges",
                    "Park entrance fees",
                    "Personalized flight certificate",
                    "Safety briefing and equipment",
                    "Game drive en route to lodge"
                  ].map((item, index) => (
                    <li key={index} className="flex items-start gap-3">
                      <FontAwesomeIcon icon="fa-check" className="w-5 h-5 text-green-600 mt-1 flex-shrink-0" />
                      <span className="text-gray-700">{item}</span>
                    </li>
                  ))}
                </ul>
              </div>

              <div className="card p-6 mb-8">
                <h3 className="text-2xl font-bold text-safari-green mb-6 flex items-center gap-2">
                  <FontAwesomeIcon icon="fa-times-circle" className="w-6 h-6 text-red-600" />
                  What's Not Included
                </h3>
                <ul className="space-y-3">
                  {[
                    "International flights",
                    "Accommodation before/after",
                    "Tips for pilot and crew",
                    "Travel insurance",
                    "Personal expenses"
                  ].map((item, index) => (
                    <li key={index} className="flex items-start gap-3">
                      <FontAwesomeIcon icon="fa-times" className="w-5 h-5 text-red-600 mt-1 flex-shrink-0" />
                      <span className="text-gray-700">{item}</span>
                    </li>
                  ))}
                </ul>
              </div>

              <div className="card p-6">
                <h3 className="text-xl font-bold text-safari-green mb-4 flex items-center gap-2">
                  <FontAwesomeIcon icon="fa-heartbeat" className="w-6 h-6" />
                  Health & Fitness
                </h3>
                <ul className="space-y-3 text-gray-700">
                  <li className="flex items-start gap-2">
                    <FontAwesomeIcon icon="fa-check-circle" className="w-5 h-5 text-safari-green mt-0.5" />
                    <span><strong>Fitness:</strong> Easy. Must be able to climb into basket using ladder and stand for 45-60 minutes.</span>
                  </li>
                  <li className="flex items-start gap-2">
                    <FontAwesomeIcon icon="fa-check-circle" className="w-5 h-5 text-safari-green mt-0.5" />
                    <span><strong>Health:</strong> Not suitable for pregnancy or serious back problems. Consult doctor if concerned.</span>
                  </li>
                  <li className="flex items-start gap-2">
                    <FontAwesomeIcon icon="fa-check-circle" className="w-5 h-5 text-safari-green mt-0.5" />
                    <span><strong>Age:</strong> Minimum 6 years. Children must be tall enough to see over basket edge.</span>
                  </li>
                </ul>
              </div>
            </div>
          </aside>
        </div>
      </div>

      <BookingModal
        isOpen={isModalOpen}
        onClose={() => setIsModalOpen(false)}
        experience="Hot Air Balloon Safari"
      />
    </div>
  );
};

export default HotAirBalloon;
